import type { Server } from 'miragejs';
import { response, readApiTransform } from 'mocks/helpers';
import { HTTP_RESPONSE_CODES } from 'mocks/constants';
import { faker } from '@faker-js/faker/locale/en';
import { generateUsers } from './purchaseOrdersHelpers';
import {
  readSettingsUsers,
  toWireSettingsUser,
  SETTINGS_USERS_ORG_ID,
} from 'mocks/data/supplierSettingsUsers';
import { stylesBulkEditProbes } from './stylesBulkEditProbeFixtureData';
import { bulkEditValidationFixtureStyles } from './stylesBulkEditValidationFixtureData';
import { contactPersonWizardUsersMap } from './contactPersonWizardUsers';
import { FILTER_VOLUME_USERS } from 'mocks/data/purchaseOrderFilterVolume';
import { seasons, groups, suppliers, contactPersons, brandOptions } from './stylesFixtureData';
import type { StyleRow, CustomFieldRowEntry } from './stylesFixtureData';
import {
  buildBulkPerformanceFixtureStyles,
  buildBulkPerformanceMixedFixtureStyles,
  buildBulkFeedbackFixtureStyles,
} from './stylesBulkFixtureData';
import {
  buildBulkTimeoutFixtureStyles,
  buildBulkTimeoutDateFixtureStyles,
  mergeCustomFieldsForBulkUpdate,
  valuesEqual,
} from './stylesBulkEditFixtures';
import {
  buildClearFieldBehaviorFixtureStyles,
  clearFieldInteractionStyleRows,
} from './stylesClearFieldFixtures';
import { styleCustomFieldDefinitions } from './stylesCustomFieldDefinitions';

/**
 * Styles Mirage handler (Delogue 2.0).
 *
 * Unlike the items handler (which ignores query params), this handler implements
 * server-side **search**, **filtering**, **sorting** and cursor pagination so that
 * the Styles filter/search e2e specs (epic DL-61856) can assert filtered *results*,
 * not just request params.
 *
 * Endpoints:
 *  - GET /api/styles                     — list with search/filter/sort/cursor
 *  - PUT /api/styles/:id                 — inline-edit save (persists to localStorage,
 *                                           records an audit-log entry per changed field)
 *  - GET /api/styles/:id/audit-log       — mock-only audit-log read surface (DL-61932)
 *  - GET /api/meta/styles/custom-fields  — custom-field definitions (none seeded)
 *  - POST /StyleNameNumberGenerator/Style/Settings — name/number generator (off)
 *  - GET /LimitationSetting              — style name char limit (none configured)
 *
 * The list envelope is intentionally { status, code, data, pagination, error } only
 * — the real Styles endpoint never returns customFieldDefinitions (see Styles.tsx),
 * so custom-field defs come from the separate meta endpoint above.
 *
 * Error/empty deviations are injected per-spec via the `apiTransform` fixture
 * (localStorage `api-transform`, consumed by mocks/helpers.ts#response) — never
 * page.route(). Because the list uses the pagination branch of response(), the
 * transform's statusCode / code / error / data overrides all apply, so specs can
 * force the `styles-fetch-error` toast (500) and the "No styles found" empty state
 * (not_found code) using the team error envelope.
 *
 * Persistence note (DL-61932/DL-61933): MirageJS re-creates its server (and this
 * module) from scratch on every full page load, so a plain in-memory array would
 * NOT survive a `page.reload()`. `stylesData`/`auditLog` are therefore seeded once
 * and then mirrored into `localStorage` on every PUT (same pattern the `apiTransform`
 * fixture already uses), so a save's effect is visible after a reload within the same
 * browser context/test — every test still starts from a clean slate, since the `page`
 * fixture opens a brand-new browser context per test.
 *
 * NOTE (DL-61877 epic — CF inline-edit, hierarchy, Advanced CF, Supplier Contact, Group/
 * Season): this file also carries CF/lookup support added for the sibling inline-edit specs
 * under `frontend/e2e/src/pages/styles/inline-edit/`:
 *  - `/api/meta/styles/custom-fields` returns a full set of CF definitions (Text, Date,
 *    single/multi AllowedValue, a NestedAllowedValue mother/child pair, and a brand/group/
 *    category-scoped "Advanced" field) instead of `[]`.
 *  - `GET /api/groups`, `GET /api/seasons`, `GET /api/styleCategories` — lookups for the
 *    Group/Season/Categories inline-edit dropdowns (`config-cols.tsx`'s `getLookupApi`).
 *  - `GET /api/organizations/:orgId/users` — Supplier Contact lookup, org-scoped so switching
 *    supplier changes the offered contacts. ALSO serves DL-62766's Mapping Request Step 1
 *    "Contact person" wizard combobox — see `CONTACT_PERSON_WIZARD_ORG_IDS` /
 *    `contactPersonWizardUsersMap` below for that epic's reserved, deterministic org ids. This
 *    exact path is ALSO registered by
 *    `purchaseOrders.ts` (PO filter dropdown) earlier in `mocks/api/index.ts`'s
 *    `disabledEndpoints()`. Mirage/Pretender resolves duplicate route registrations by
 *    OVERWRITING the route-recognizer NFA state for an identical path template — i.e. the
 *    LAST-registered handler for a given path wins outright, not a fallback chain (verified
 *    against `route-recognizer`'s `RouteRecognizer.prototype.add`, which reassigns
 *    `currentState.handlers` on every `add()` call reaching the same trie state). Because
 *    `stylesEndpoints(server)` runs after `purchaseOrdersEndpoints(server)`, our handler below
 *    fully replaces that one for this path — so it explicitly replicates the PO handler's
 *    generic fallback (same `schema.db.apiUsers` seed via `generateUsers`, same shape) for any
 *    orgId outside our seeded style-supplier ids, and only branches to supplier-scoped data for
 *    orgId in {44, 45, 46}. Do not remove the fallback branch without confirming the PO filter
 *    dropdown coverage still passes.
 */

/** Matches StyleStateColors keys in constants/styleState — all states are seeded. */
const STYLE_STATES = [
  'unpublished',
  'published',
  'workinprogress',
  'delivered',
  'cancelled',
  'deleted',
];

/** Name parts guarantee search/text-filter fixtures: "Basic" (adj) and "shirt" (type). */
const styleAdjectives = [
  'Basic',
  'Classic',
  'Premium',
  'Casual',
  'Slim',
  'Relaxed',
  'Vintage',
  'Modern',
];
const styleTypes = ['Shirt', 'Trousers', 'Jacket', 'Dress', 'Skirt', 'Sweater', 'Coat', 'Blouse'];

const categories = [
  { id: 87, customId: 'TOP', name: 'Tops' },
  { id: 88, customId: 'BOT', name: 'Bottoms' },
  { id: 89, customId: 'OUT', name: 'Outerwear' },
  { id: 90, customId: 'KNT', name: 'Knitwear' },
];

/**
 * Supplier org id -> that supplier's contact persons (DL-61912 — Supplier Contact dropdown
 * must be filtered to the row's *own* supplier's users, not the full org-wide list). Disjoint
 * sets so switching supplier on a row visibly changes the offered contacts. Keyed by the
 * `suppliers[].id` values above.
 */
const supplierContactsMap: Record<number, Array<{ id: string; name: string }>> = {
  44: [contactPersons[0], contactPersons[1]], // Ningbo garment supplier -> Sara Johnson, Mike Chen
  45: [contactPersons[2], contactPersons[3]], // Shanghai apparel co. -> Anna Larsen, James Park
  46: [contactPersons[4], contactPersons[5]], // Guangzhou textile factory -> Lina Sorensen, Tom Becker
};

// Styles CF definitions (DL-61877 epic) now live in `stylesCustomFieldDefinitions.ts` — split
// out alongside the DL-62594 bulk-edit fixtures to keep this file under the 800-line cap (same
// pattern as `itemsFixtureData.ts` for `items.ts`). See that file for the full rationale.

/**
 * CF 707 ("Sustainability Rating") is scoped to Group=Menswear(71) AND Category=Tops(87),
 * mirroring `StyleCustomFieldScopeValidator.AppliesToStyle`'s AND-across-configured-dimensions
 * rule (`Src/Delogue.Application/Styles/CustomFields/StyleCustomFieldScopeValidator.cs`): a
 * style applies only when EVERY dimension the field actually carries associations for is
 * matched — brand/group each OR-match within their own list, but a field configured on
 * multiple dimensions requires all of them to pass. Here only group + category are
 * configured, so a style must be Menswear AND carry the Tops category.
 */
const SUSTAINABILITY_SCOPE = { groupId: 71, categoryId: 87 };
const computeSustainabilityAppliesToStyle = (
  groupId: number | null | undefined,
  categoryIds: number[]
): boolean =>
  groupId === SUSTAINABILITY_SCOPE.groupId && categoryIds.includes(SUSTAINABILITY_SCOPE.categoryId);

const TOTAL = 150;
const perPage = 100;

faker.seed(4242);

const generateStyle = (i: number): StyleRow => {
  const adjective = styleAdjectives[i % styleAdjectives.length];
  const type = styleTypes[Math.floor(i / styleAdjectives.length) % styleTypes.length];
  // Decouple brand from the name (adjective) so that — because the table sorts by
  // name (adjective-first) — every brand's styles are spread across the sort order.
  // Keying brand on `i % len` would tie each brand to one adjective and cluster a
  // multi-brand filter's visible (virtualised) rows into a single brand.
  const brand = brandOptions[Math.floor(i / styleAdjectives.length) % brandOptions.length];
  const contact = contactPersons[i % contactPersons.length];
  const supplier = suppliers[i % suppliers.length];
  const supplierContact = contactPersons[(i + 1) % contactPersons.length];
  const categoryCount = i % 3; // 0, 1 or 2 categories
  const selectedCategories = Array.from(
    { length: categoryCount },
    (_, c) => categories[(i + c) % categories.length]
  );
  // Spread lastUpdated across ~150 days, deterministic per row (no runtime clock).
  const day = String((i % 28) + 1).padStart(2, '0');
  const month = String((i % 12) + 1).padStart(2, '0');
  const lastUpdated = `2025-${month}-${day}T10:00:00`;

  // Every row carries an explicit entry for the scoped "Sustainability Rating" (CF 707) CF —
  // `appliesToStyle` must be present (not just absent-defaults-true) so the FE's
  // `makeScopedCell` renders "Not applicable" for out-of-scope rows instead of defaulting to
  // applies (see `getRowCustomFieldApplies` in cellRenderers.tsx). No rating value is
  // pre-assigned on the generic seed; the pinned demo rows below override this with a value.
  const sustainabilityApplies = computeSustainabilityAppliesToStyle(
    groups[i % groups.length].id,
    selectedCategories.map(c => c.id)
  );
  const customFields: CustomFieldRowEntry[] = [
    {
      id: 707,
      type: 'allowedvalue',
      value: null,
      appliesToStyle: sustainabilityApplies,
    },
  ];

  return {
    id: i,
    name: `${adjective} ${type} ${i}`,
    customId: `ST-${1000 + i}`,
    description: faker.helpers.maybe(() => faker.commerce.productDescription()) ?? null,
    state: STYLE_STATES[i % STYLE_STATES.length],
    logo: '',
    season: seasons[i % seasons.length],
    group: groups[i % groups.length],
    brand: {
      id: brand.id,
      customId: brand.userDefinedId ?? '',
      name: brand.name,
      contactPersonId: contact.id,
      contactPersonName: contact.name,
    },
    supplier: {
      id: supplier.id,
      customId: supplier.customId,
      name: supplier.name,
      contactPersonId: supplierContact.id,
      contactPersonName: supplierContact.name,
    },
    categories: selectedCategories,
    styleColors: [],
    customFields,
    properties: [],
    lastUpdated,
    readyForExport: i % 2 === 0,
  };
};

/**
 * Deterministic fixture for the audit-log + persistence-after-refresh checks (Testmo
 * 2373100 / 2373073, DL-61932/DL-61933). Prepended to the generated rows (like the
 * DL-61875 ids 1/2 pin below) so it lands on the mocked page-1 slice with a stable
 * id/description regardless of faker output.
 */
const auditPersistenceFixtureStyle = (): StyleRow => ({
  id: 90001,
  name: 'Audit Trail Fixture Style',
  customId: 'ST-90001',
  description: 'Original description for persistence and audit checks.',
  state: 'unpublished',
  logo: '',
  season: seasons[0],
  group: groups[0],
  brand: {
    id: brandOptions[0].id,
    customId: brandOptions[0].userDefinedId ?? '',
    name: brandOptions[0].name,
    contactPersonId: contactPersons[0].id,
    contactPersonName: contactPersons[0].name,
  },
  supplier: {
    id: suppliers[0].id,
    customId: suppliers[0].customId,
    name: suppliers[0].name,
    contactPersonId: contactPersons[1].id,
    contactPersonName: contactPersons[1].name,
  },
  categories: [],
  styleColors: [],
  customFields: [],
  properties: [],
  lastUpdated: '2026-01-01T00:00:00',
  readyForExport: false,
});

// Large-batch + named-pair bulk-edit fixtures for the DL-62594 epic ("update execution,
// feedback & performance") live in `stylesBulkEditFixtures.ts` — split out purely to keep this
// file under the repo's 800-line cap. See that file's header comments for the full rationale
// (why these are real persisted rows rather than an `apiTransform` override, the `ST-9…`
// customId sort-safety note, and the AW25/AW26 season-picker note).

const buildDefaultStylesData = (): StyleRow[] => {
  const generated = Array.from({ length: TOTAL }, (_, i) => generateStyle(i + 1));
  // DL-61893/94/95 — pin deterministic descriptions for ids 1 & 2 (both in editable
  // states — STYLE_STATES[1 % 6] = 'published', STYLE_STATES[2 % 6] = 'workinprogress')
  // so the editing-flow E2E coverage (enter edit → save → click-between-cells →
  // navigate-away) has stable, non-empty starting values regardless of faker output.
  generated[0].description = 'Style A description used by the DL-61875 editing-flow E2E coverage.';
  generated[1].description = 'Style B description used by the DL-61875 editing-flow E2E coverage.';
  // IMPORTANT: all fixture blocks below are appended AFTER `generated`, not spliced before it.
  // `styleA`/`styleB`/`styleF`/`styleG` (DL-61877 epic) index into `stylesData` by FIXED
  // POSITION (`stylesData[1]`, `[2]`, `[6]`, `[7]`, documented as "generated id N is shifted one
  // slot to index N" — i.e. they assume index 0 is the audit fixture and index 1..150 are
  // `generated[0..149]` in order). Inserting any fixture block BEFORE `generated` would shift
  // every one of those indices by its length, silently repointing that epic's hardcoded
  // mutations onto the new fixtures instead (verified empirically — that is exactly what
  // happened before this fix).
  return [
    auditPersistenceFixtureStyle(),
    ...generated,
    ...buildBulkPerformanceFixtureStyles(),
    ...buildBulkPerformanceMixedFixtureStyles(),
    ...buildBulkFeedbackFixtureStyles(),
    ...buildBulkTimeoutFixtureStyles(),
    ...buildBulkTimeoutDateFixtureStyles(),
    ...buildClearFieldBehaviorFixtureStyles(),
    ...bulkEditValidationFixtureStyles,
    // DL-62706 epic ("Bulk edit clear fields: interactions, dependency, apply & audit log") —
    // hierarchy CF cascade + apply/audit-log rows, see stylesClearFieldFixtures.ts.
    ...clearFieldInteractionStyleRows,
  ];
};

const STYLES_STORAGE_KEY = '__mock_styles_data__';
const AUDIT_STORAGE_KEY = '__mock_styles_audit_log__';

export type StyleAuditLogEntry = {
  id: number;
  styleId: number;
  field: string;
  oldValue: unknown;
  newValue: unknown;
  changedAt: string;
  changedBy: string;
};

/** Top-level scalar Style fields the inline-edit flow can PUT and we track for audit purposes. */
const AUDITABLE_FIELDS = ['name', 'description', 'customId', 'state', 'readyForExport'] as const;

const readStorage = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const writeStorage = (key: string, value: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* best-effort — a full/unavailable localStorage shouldn't crash the mock */
  }
};

let stylesData: StyleRow[] = readStorage(STYLES_STORAGE_KEY, undefined as unknown as StyleRow[]);
// Only seed a fresh dataset (including every BulkEditProbe* fixture pushed below) when there is
// no persisted state yet. Restoring from `localStorage` (e.g. after a bulk-edit PUT persists a
// change and the spec reloads within the same test/browser context) must NOT re-push the
// pristine probe fixtures on top of the already-mutated restored array — that would duplicate
// every probe row (one stale pre-mutation copy alongside the real, updated one).
const builtStylesFresh = !stylesData;
if (builtStylesFresh) {
  stylesData = buildDefaultStylesData();
  writeStorage(STYLES_STORAGE_KEY, stylesData);
}

let auditLog: StyleAuditLogEntry[] = readStorage(AUDIT_STORAGE_KEY, [] as StyleAuditLogEntry[]);
let nextAuditId = auditLog.reduce((max, entry) => Math.max(max, entry.id), 0) + 1;

const persistStyles = () => writeStorage(STYLES_STORAGE_KEY, stylesData);
const persistAuditLog = () => writeStorage(AUDIT_STORAGE_KEY, auditLog);

/**
 * `response()` (mocks/helpers.ts) applies the `apiTransform` fixture's override only when
 * building the final HTTP response — it does not stop a handler's own logic from running
 * first. Without this guard, a PUT forced to fail via `apiTransform` would still mutate
 * `stylesData`/`auditLog` before the error status was applied, silently persisting a
 * "failed" save (defeating the DL-61933 "no phantom persistence on a failed save" case).
 * Mirrors the same substring-match contract as `response()` so a transform aimed at this
 * URL with a >=400 `statusCode` short-circuits the mutation entirely.
 */
const isForcedToFail = (url: string): boolean => {
  try {
    const raw = localStorage.getItem('api-transform');
    if (!raw) return false;
    const transform = JSON.parse(raw) as { url?: string; statusCode?: number };
    return !!transform.url && url.includes(transform.url) && (transform.statusCode ?? 0) >= 400;
  } catch {
    return false;
  }
};

// `valuesEqual` (structural equality for audit-diffing bulk-edit fields) lives in
// `stylesBulkEditFixtures.ts` — split out alongside the fixture builders to keep this file
// under the 800-line cap (DL-62594). It's only used by the bulk PUT handler below.

/**
 * DL-61877 epic — deterministic rows for the CF widget-type / hierarchy / Advanced-CF /
 * Supplier Contact inline-edit e2e suite (`e2e/src/pages/styles/inline-edit/`). Ids below are
 * at index N in `stylesData` (not N-1): index 0 is the DL-61932/33 audit/persistence fixture
 * (Testmo 2373100/2373073, id 90001) prepended ahead of the generated rows, so generated id N
 * is shifted one slot to index N. All rows are in an editable style state
 * (STYLE_STATES[id % 6] not in ['delivered','cancelled','deleted']).
 *
 *  - id 1 "Classic Shirt 1" (published) — the primary "everything populated" row: every CF
 *    widget type has a value, the Print Type/Placement hierarchy mother has a value (child is
 *    editable + filtered to its children), and Sustainability Rating APPLIES (Menswear + Tops)
 *    with a rating already set. Supplier is Shanghai apparel co. (id 45).
 *  - id 2 "Premium Shirt 2" (workinprogress) — hierarchy mother (Print Type) has NO value, so
 *    the child (Print Placement) must render disabled/non-interactive. Sustainability Rating
 *    does NOT apply — fails on the group dimension only (Womenswear, but carries Tops).
 *  - id 6 "Vintage Shirt 6" (unpublished) — Sustainability Rating does NOT apply — fails on
 *    the category dimension only (Menswear, but carries Bottoms, not Tops) — proves the AND
 *    is per-dimension, not "any mismatch anywhere".
 *  - id 7 "Modern Shirt 7" (published) — no supplier assigned, so Supplier Contact must
 *    render the "Select a supplier first" placeholder instead of a dropdown.
 */
const styleA = stylesData[1];
styleA.group = { id: 71, customId: 'MEN', name: 'Menswear' };
styleA.categories = [{ id: 87, customId: 'TOP', name: 'Tops' }];
styleA.supplier = {
  id: 45,
  customId: 'S-45',
  name: 'Shanghai apparel co.',
  contactPersonId: contactPersons[2].id,
  contactPersonName: contactPersons[2].name,
};
styleA.customFields = [
  { id: 701, type: 'text', value: { id: 0, value: 'Soft brushed cotton weave, pre-shrunk.' } },
  { id: 702, type: 'date', value: { id: 0, value: '2026-08-01' } },
  { id: 703, type: 'allowedvalue', value: { id: 7032, value: 'Regular' } },
  {
    id: 704,
    type: 'allowedvalue',
    values: [
      { id: 7041, value: 'Cotton' },
      { id: 7044, value: 'Elastane' },
    ],
  },
  { id: 705, type: 'nestedallowedvalue', value: { id: 7051, value: 'Screen Print' } },
  { id: 706, type: 'nestedallowedvalue', value: { id: 7061, value: 'Chest' } },
  { id: 707, type: 'allowedvalue', value: { id: 7073, value: 'Gold' }, appliesToStyle: true },
];

const styleB = stylesData[2];
styleB.group = { id: 72, customId: 'WMN', name: 'Womenswear' };
styleB.categories = [{ id: 87, customId: 'TOP', name: 'Tops' }];
styleB.supplier = {
  id: 46,
  customId: 'S-46',
  name: 'Guangzhou textile factory',
  contactPersonId: contactPersons[4].id,
  contactPersonName: contactPersons[4].name,
};
// No entry for CF 705/706 — the hierarchy mother genuinely has no value.
styleB.customFields = [
  { id: 707, type: 'allowedvalue', value: null, appliesToStyle: false }, // group mismatch only
];

const styleF = stylesData[6]; // id 6
styleF.group = { id: 71, customId: 'MEN', name: 'Menswear' };
styleF.categories = [{ id: 88, customId: 'BOT', name: 'Bottoms' }];
styleF.customFields = [
  { id: 707, type: 'allowedvalue', value: null, appliesToStyle: false }, // category mismatch only
];

const styleG = stylesData[7]; // id 7
const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';
styleG.supplier = {
  id: 0,
  customId: '',
  name: '',
  contactPersonId: EMPTY_GUID,
  contactPersonName: '',
};

if (builtStylesFresh) {
  // Bulk-edit field-availability probe fixtures (DL-62593) live in
  // `stylesBulkEditProbeFixtureData.ts` — split out to keep this file under the 800-line cap.
  stylesData.push(...stylesBulkEditProbes);
  // The initial fresh-build write above (`writeStorage` right after `buildDefaultStylesData()`)
  // ran BEFORE these probes were appended, so it persisted a probe-less snapshot. Persist again
  // now that the in-memory array is complete, or every reload after the very first page load in
  // this browser storage would restore that probe-less snapshot and never see them again.
  persistStyles();
}

const getSortValue = (row: StyleRow, property: string): string | number => {
  switch (property) {
    case 'name':
      return row.name.toLowerCase();
    case 'number':
      return row.customId.toLowerCase();
    case 'state':
      return row.state;
    case 'brand':
      return row.brand?.name?.toLowerCase() ?? '';
    case 'brandcontactperson':
      return row.brand?.contactPersonName?.toLowerCase() ?? '';
    case 'supplier':
      return row.supplier?.name?.toLowerCase() ?? '';
    case 'season':
      return row.season?.name?.toLowerCase() ?? '';
    case 'group':
      return row.group?.name?.toLowerCase() ?? '';
    case 'description':
      return row.description?.toLowerCase() ?? '';
    case 'readyforexport':
      return row.readyForExport ? 1 : 0;
    case 'lastupdated':
      return row.lastUpdated;
    default:
      return '';
  }
};

const sortRows = (rows: StyleRow[], property: string, order: string): StyleRow[] => {
  const sorted = [...rows].sort((a, b) => {
    const av = getSortValue(a, property);
    const bv = getSortValue(b, property);
    if (av < bv) return -1;
    if (av > bv) return 1;
    return a.id - b.id; // stable tiebreak
  });
  return order === 'desc' ? sorted.reverse() : sorted;
};

const includesCI = (haystack: string | null | undefined, needle: string): boolean =>
  (haystack ?? '').toLowerCase().includes(needle.toLowerCase());

const idSet = (raw: string): Set<number> =>
  new Set(
    raw
      .split(',')
      .map(v => Number(v))
      .filter(v => Number.isFinite(v))
  );

const applyFilters = (requestUrl: string): StyleRow[] => {
  const params = new URL(requestUrl, 'http://localhost').searchParams;
  let rows = stylesData;

  const search = params.get('search');
  if (search) rows = rows.filter(r => includesCI(r.name, search) || includesCI(r.customId, search));

  const name = params.get('name');
  if (name) rows = rows.filter(r => includesCI(r.name, name));

  const number = params.get('number');
  if (number) rows = rows.filter(r => includesCI(r.customId, number));

  const description = params.get('description');
  if (description) rows = rows.filter(r => includesCI(r.description, description));

  const state = params.get('state');
  if (state) {
    const set = new Set(state.split(','));
    rows = rows.filter(r => set.has(r.state));
  }

  const brand = params.get('brand');
  if (brand) {
    const set = idSet(brand);
    rows = rows.filter(r => r.brand != null && set.has(r.brand.id));
  }

  const brandContact = params.get('brandcontactperson');
  if (brandContact) {
    const set = new Set(brandContact.split(','));
    rows = rows.filter(r => r.brand != null && set.has(r.brand.contactPersonId));
  }

  const supplier = params.get('supplier');
  if (supplier) {
    const set = idSet(supplier);
    rows = rows.filter(r => r.supplier != null && set.has(r.supplier.id));
  }

  const supplierContact = params.get('suppliercontactperson');
  if (supplierContact) {
    const set = new Set(supplierContact.split(','));
    rows = rows.filter(r => r.supplier != null && set.has(r.supplier.contactPersonId));
  }

  const season = params.get('season');
  if (season) {
    const set = idSet(season);
    rows = rows.filter(r => r.season != null && set.has(r.season.id));
  }

  const group = params.get('group');
  if (group) {
    const set = idSet(group);
    rows = rows.filter(r => r.group != null && set.has(r.group.id));
  }

  const category = params.get('category');
  if (category) {
    const set = idSet(category);
    rows = rows.filter(r => r.categories.some(c => set.has(c.id)));
  }

  const readyForExport = params.get('readyforexport');
  if (readyForExport) {
    const want = readyForExport === 'true';
    rows = rows.filter(r => Boolean(r.readyForExport) === want);
  }

  const lastUpdated = params.get('lastupdated');
  if (lastUpdated) {
    const [from, to] = lastUpdated.split(',');
    rows = rows.filter(r => {
      const day = r.lastUpdated.slice(0, 10);
      return (!from || day >= from) && (!to || day <= to);
    });
  }

  const sortProperty = params.get('sort[0].property');
  const sortOrder = (params.get('sort[0].order') ?? 'asc').toLowerCase();
  if (sortProperty) rows = sortRows(rows, sortProperty, sortOrder);

  return rows;
};

/**
 * Reverses `objCamelToPascal` (`lib/api/utils`) so a PUT request body — PascalCased
 * on the wire (see `api/styles.ts` `apiStyles.put.byId`) — can be merged back onto
 * the camelCase mock fixtures. Mirrors the equivalent helper in `items.ts`.
 */
const pascalKeysToCamel = (obj: unknown): unknown => {
  if (Array.isArray(obj)) {
    return obj.map(pascalKeysToCamel);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.entries(obj as Record<string, unknown>).reduce(
      (acc, [key, value]) => {
        const camelKey = key.charAt(0).toLowerCase() + key.slice(1);
        acc[camelKey] = pascalKeysToCamel(value);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }
  return obj;
};

export const stylesEndpoints = (server: Server) => {
  server.get('/api/styles', (_schema, request) => {
    const url = new URL(request.url, 'http://localhost');
    const cursor = url.searchParams.get('cursor');

    // A cursor request carries only `cursor` (getStyles drops the filters), so page
    // the full dataset. The active sort is encoded into the cursor as
    // `page~property~order` so a non-default sort is preserved on later pages
    // (getStyles doesn't resend sort with the cursor). Filtered result sets are
    // < perPage, so the UI only requests a cursor for the unfiltered list.
    if (cursor) {
      const [pageStr, cursorSortProperty = '', cursorSortOrder = 'asc'] = cursor.split('~');
      const page = Number(pageStr) || 1;
      // No sort requested: page the live array directly (id-order) rather than a
      // sorted snapshot computed at module init, which would go stale after a PUT
      // replaces an element's object reference (see styles.ts PUT handler below).
      const ordered = cursorSortProperty
        ? sortRows(stylesData, cursorSortProperty, cursorSortOrder)
        : stylesData;
      const start = page * perPage;
      const end = Math.min(start + perPage, ordered.length);
      const pageData = ordered.slice(start, end);
      return response(request.url, pageData, {
        status: 'success',
        code: 'ok',
        pagination: {
          cursor: `${page + 1}~${cursorSortProperty}~${cursorSortOrder}`,
          hasMore: end < ordered.length,
        },
        error: { details: [] },
        statusCode: HTTP_RESPONSE_CODES.OK,
      });
    }

    const sortProperty = url.searchParams.get('sort[0].property') ?? '';
    const sortOrder = (url.searchParams.get('sort[0].order') ?? 'asc').toLowerCase();
    const rows = applyFilters(request.url);
    const pageData = rows.slice(0, perPage);
    const hasMore = rows.length > perPage;
    return response(request.url, pageData, {
      status: 'success',
      code: 'ok',
      pagination: {
        cursor: `1~${sortProperty}~${sortOrder}`,
        hasMore,
      },
      error: { details: [] },
      statusCode: HTTP_RESPONSE_CODES.OK,
    });
  });

  /**
   * Inline-edit save. Persists the (PascalCased) write onto the `stylesData` fixture —
   * mirrored into `localStorage` (see file header) so a later reload/refetch sees it —
   * and records an audit-log entry (DL-61932) for each `AUDITABLE_FIELDS` entry whose
   * value actually changed (a no-op edit records nothing).
   *
   * When the active `apiTransform` forces a failure status for this URL, the mutation
   * and audit logging are skipped entirely before `response()` builds the (overridden)
   * error envelope, so a failed save can never appear to have been persisted or audited.
   */
  server.put('/api/styles/:id', (_schema, request) => {
    const id = Number(request.params.id);

    // A forced failure must not mutate anything — see isForcedToFail's doc comment. Only
    // `statusCode` is passed here as a fallback: `status`/`code` are deliberately omitted so
    // response()'s own transform handling is the sole source of the error body, rather than
    // this early-exit leaking a hardcoded 'internal_server_error' when the transform's actual
    // code/error fields are unset.
    if (isForcedToFail(request.url)) {
      return response(request.url, undefined, { statusCode: HTTP_RESPONSE_CODES.SERVER_ERROR });
    }

    const index = stylesData.findIndex(style => style.id === id);
    if (index === -1) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.ENTITY_NOT_FOUND,
        status: 'fail',
        code: 'resource_error.style_not_found',
        error: { details: [{ type: 'resource_error.style_not_found', value: String(id) }] },
      });
    }
    if (!request.requestBody) {
      return response(request.url, undefined, {
        statusCode: HTTP_RESPONSE_CODES.BAD_INPUT,
        status: 'fail',
        code: 'validation_error.required_field',
        error: { details: [{ type: 'validation_error.required_field', value: 'Bad input' }] },
      });
    }

    const body = pascalKeysToCamel(JSON.parse(request.requestBody)) as Record<string, unknown>;
    const current = stylesData[index];
    const changed: Array<{ field: string; oldValue: unknown; newValue: unknown }> = [];

    AUDITABLE_FIELDS.forEach(field => {
      if (!(field in body)) return;
      const oldValue = (current as unknown as Record<string, unknown>)[field] ?? null;
      const newValue = body[field] ?? null;
      if (oldValue === newValue) return;
      changed.push({ field, oldValue, newValue });
    });

    const updated: StyleRow = {
      ...current,
      ...(body as Partial<StyleRow>),
      id,
      lastUpdated: new Date().toISOString(),
    };
    stylesData = [...stylesData.slice(0, index), updated, ...stylesData.slice(index + 1)];
    persistStyles();

    if (changed.length > 0) {
      const changedAt = new Date().toISOString();
      const newEntries = changed.map(c => ({
        id: nextAuditId++,
        styleId: id,
        field: c.field,
        oldValue: c.oldValue,
        newValue: c.newValue,
        changedAt,
        changedBy: 'John Smith',
      }));
      auditLog = [...auditLog, ...newEntries];
      persistAuditLog();
    }

    return response(request.url, updated, {
      status: 'success',
      code: 'ok',
      statusCode: HTTP_RESPONSE_CODES.OK,
    });
  });

  /**
   * PUT /api/styles — bulk-edit "Apply changes" (DL-62594 epic: Testmo 1577182/1577183/1577263/
   * 1577259/1577264/1577267/1577272). `apiStyles.put.bulkUpdate` sends a PascalCased array, one
   * entry per selected style, each carrying `Id` plus only the fields the bulk-edit form actually
   * changed (`useBulkEditSubmit`) — mirrors the items.ts `PUT /api/items` bulk handler.
   *
   * Every id in the request is "touched" (lastUpdated bumped) regardless of whether any of its
   * own field values actually changed, matching the single-row PUT handler's unconditional
   * `lastUpdated` bump and modelling a bulk "set" operation that writes every targeted row. Audit
   * entries (DL-61932's mock-only surface, extended here beyond the single-row `AUDITABLE_FIELDS`
   * to cover the bulk-editable columns — season/group/brand/supplier/categories/etc., all nested
   * objects) are recorded only for fields whose value genuinely differs per style — see
   * `valuesEqual` — so a style whose selected value already matched the bulk-set value gets no
   * spurious entry, same "no-op logs nothing" contract as the single-row path.
   *
   * `customFields` is a special case: the wire payload only carries the touched fields as
   * `{ id, value }` (no `type`), so it's merged onto the row's existing array via
   * `mergeCustomFieldsForBulkUpdate` (resolving each entry's `type` from the CF definitions)
   * rather than letting the generic spread below overwrite the whole array.
   *
   * This mock-only audit-log endpoint is the D2-observable proxy this epic uses for "activity log
   * records each change per style" (Testmo 1577183/1577272) — the real legacy-server activity log
   * entry/format is explicitly OUT OF SCOPE for this mock suite (see the spec files' headers);
   * nothing here renders it or asserts its wire format.
   *
   * Skips the mutation entirely (mirrors `isForcedToFail`'s doc comment on the single-row PUT)
   * when the active `apiTransform` forces a failure for this URL, so a failed "Apply changes"
   * can never appear to have been persisted for any of the targeted styles.
   */
  server.put('/api/styles', (_schema, request) => {
    let body: unknown = [];
    try {
      body = JSON.parse(request.requestBody || '[]');
    } catch {
      body = [];
    }
    const rows = (Array.isArray(body) ? body : [body]) as Record<string, unknown>[];
    const updatedRows: StyleRow[] = [];

    if (!isForcedToFail(request.url)) {
      const changedAt = new Date().toISOString();
      rows.forEach(row => {
        if (!row || typeof row !== 'object') return;
        const id = Number(row.Id ?? row.id);
        const index = stylesData.findIndex(style => style.id === id);
        if (index === -1) return;

        const current = stylesData[index];
        const camelBody = pascalKeysToCamel(row) as Record<string, unknown>;

        // The bulk-edit form's dropdown option shape is `{ id, label }` (`ComboBoxOption` — see
        // e.g. `config-cols.tsx`'s inline Season/Brand `onChange` handlers, which explicitly
        // re-derive `name: selected.label`), but `fieldTransforms.season` reads `season.name`
        // off that SAME option object, which has no `name` key — so the wire's `Season` object
        // only ever carries `{ id }` with the display name silently dropped. A REAL backend
        // wouldn't round-trip the client's guess at a display name at all — it would resolve the
        // full season from `SeasonId` server-side, exactly like the single-row PUT handler's
        // caller-supplied `season` object is trusted directly because there `getBasePayload`/the
        // inline cell already send a complete `{ id, name }`. Mirror that "resolve by id"
        // behaviour here so the mocked response (and thus the post-Apply refetch) shows the real
        // season name instead of propagating the wire's incomplete fragment.
        if (typeof camelBody.seasonId === 'number') {
          const resolved = seasons.find(s => s.id === camelBody.seasonId);
          if (resolved) camelBody.season = resolved;
        }

        // The wire payload's `customFields` carries only the touched fields as `{ id, value }`
        // (no `type`) — merge by customFieldId onto the row's existing array (resolving each
        // entry's `type` from the CF definitions) rather than letting the generic spread below
        // overwrite the whole array; see `mergeCustomFieldsForBulkUpdate`'s own doc comment.
        if ('customFields' in camelBody) {
          camelBody.customFields = mergeCustomFieldsForBulkUpdate(
            current.customFields,
            camelBody.customFields
          );
        }

        Object.entries(camelBody).forEach(([field, newValue]) => {
          if (field === 'id' || field.endsWith('Id')) return; // 'Id' companions duplicate the object field below
          const oldValue = (current as unknown as Record<string, unknown>)[field] ?? null;
          if (valuesEqual(oldValue, newValue ?? null)) return;
          auditLog = [
            ...auditLog,
            {
              id: nextAuditId++,
              styleId: id,
              field,
              oldValue,
              newValue: newValue ?? null,
              changedAt,
              changedBy: 'John Smith',
            },
          ];
        });

        const updated: StyleRow = {
          ...current,
          ...(camelBody as Partial<StyleRow>),
          id,
          lastUpdated: changedAt,
        };
        stylesData = [...stylesData.slice(0, index), updated, ...stylesData.slice(index + 1)];
        updatedRows.push(updated);
      });
      persistStyles();
      persistAuditLog();
    }

    return response(request.url, updatedRows, {
      status: 'success',
      code: 'ok',
      error: { details: [] },
      statusCode: HTTP_RESPONSE_CODES.OK,
    });
  });

  // Mock-only — no real audit/activity UI exists anywhere in the D2 frontend yet
  // (grepped — nothing beyond a stray code comment). Per DL-61932's own allowance
  // ("if no audit mechanism exists yet in the mock layer, add a minimal one"), the
  // e2e spec reads this directly via `page.evaluate(() => fetch(...))`.
  server.get('/api/styles/:id/audit-log', (_schema, request) => {
    const id = Number(request.params.id);
    const entries = auditLog.filter(entry => entry.styleId === id);
    return response(request.url, entries, {
      status: 'success',
      code: 'ok',
      error: { details: [] },
      statusCode: HTTP_RESPONSE_CODES.OK,
    });
  });

  // DL-61877 — was `[]`; now seeded with one CF per widget type + hierarchy pair + scoped field.
  server.get('/api/meta/styles/custom-fields', (_schema, request) =>
    response(request.url, styleCustomFieldDefinitions, {
      status: 'success',
      code: 'ok',
      pagination: { cursor: '', hasMore: false },
      error: { details: [] },
      statusCode: HTTP_RESPONSE_CODES.OK,
    })
  );

  /**
   * Style name/number generator settings. `useStyleGeneratorSettings.ts` reads
   * `AdminStyleNameGeneratorSettings`/`AdminStyleNumberGeneratorSettings` directly off the
   * response body — it is a legacy 1-segment REST-style endpoint that does NOT go through the
   * team's `{status,code,data}` envelope (same shape as `LimitationSetting` below), so this
   * uses `attribs.body` for a raw passthrough rather than the default enveloped `response()`
   * call, which would nest the payload under an extra `.data` the hook never unwraps.
   * `response()` does not apply the `apiTransform` override to `attribs.body` (it only applies
   * to the `body` param, which `attribs.body` bypasses), so the override is computed by hand
   * here — mirrors `items.ts`'s `/ItemNameNumberGenerator/Item/Settings` handler.
   */
  server.post('/StyleNameNumberGenerator/Style/Settings', (_schema, request) => {
    const transform = readApiTransform();
    const overridden = !!transform?.url && request.url.includes(transform.url);
    const body =
      overridden && transform?.data
        ? transform.data
        : {
            AdminStyleNameGeneratorSettings: { IsActive: false },
            AdminStyleNumberGeneratorSettings: { IsActive: false },
            // Not part of the real endpoint's shape, but surfaced so a forced apiTransform
            // failure (code/error, no data override) is still observable to a test that reads
            // the response body directly instead of relying on the hook to absorb it silently.
            ...(overridden && (transform?.code || transform?.error)
              ? { code: transform.code, error: transform.error }
              : {}),
          };
    return response(request.url, undefined, {
      statusCode: overridden && transform?.statusCode ? transform.statusCode : 200,
      body,
    });
  });

  // No configured name char-limit — the Styles page falls back to its default.
  server.get('/LimitationSetting', (_schema, request) =>
    response(request.url, { StyleLimitationSettings: [] })
  );

  // DL-61877 — Group / Season / Categories inline-edit dropdown lookups
  // (`config-cols.tsx`'s `getLookupApi('group' | 'season' | 'styleCategories')`).
  server.get('/api/groups', (_schema, request) => {
    const search = (
      new URL(request.url, 'http://localhost').searchParams.get('search') ?? ''
    ).toLowerCase();
    const data = groups.filter(g => g.name.toLowerCase().includes(search));
    return response(request.url, data, { status: 'success', code: 'ok', error: { details: [] } });
  });

  server.get('/api/seasons', (_schema, request) => {
    const search = (
      new URL(request.url, 'http://localhost').searchParams.get('search') ?? ''
    ).toLowerCase();
    const data = seasons.filter(s => s.name.toLowerCase().includes(search));
    return response(request.url, data, { status: 'success', code: 'ok', error: { details: [] } });
  });

  server.get('/api/styleCategories', (_schema, request) => {
    const search = (
      new URL(request.url, 'http://localhost').searchParams.get('search') ?? ''
    ).toLowerCase();
    const data = categories.filter(c => c.name.toLowerCase().includes(search));
    return response(request.url, data, { status: 'success', code: 'ok', error: { details: [] } });
  });

  // DL-61877 — Supplier Contact inline-edit lookup (`getLookupApi('supplierContactPerson')`),
  // org-scoped so the dropdown is filtered to the row's own supplier's users. See the file-level
  // note above: this handler REPLACES purchaseOrders.ts's registration of the same exact path
  // (last-registered wins), so it explicitly preserves that handler's generic fallback for any
  // orgId that isn't one of our seeded style suppliers.
  server.get('/api/organizations/:orgId/users', (schema, request) => {
    const orgId = Number(request.params.orgId);
    const search = (request.queryParams.search as string | undefined)?.toLowerCase() ?? '';

    // Supplier portal Settings (dl-60441 / dl-60445) — org 63 is the mock supplier admin org.
    // Serves the Users-tab grid (all statuses + roles + brand access) from a shared store so
    // invited users (POST in suppliersApi.ts) show up here without a full reload.
    if (orgId === SETTINGS_USERS_ORG_ID) {
      const order = (request.queryParams.order as string | undefined)?.toLowerCase() ?? 'asc';
      const sort = (request.queryParams.sort as string | undefined)?.toLowerCase() ?? '';
      const sortValue = (u: (typeof users)[number]): string => {
        if (sort === 'email') return u.email;
        if (sort === 'role') return u.role;
        if (sort === 'status') return u.status;
        if (sort === 'lastname') return u.lastName;
        if (sort === 'firstname') return u.firstName;
        return `${u.firstName} ${u.lastName}`; // name (default)
      };

      let users = readSettingsUsers();
      if (search) {
        users = users.filter(u =>
          [u.firstName, u.lastName, u.email].some(field => field.toLowerCase().includes(search))
        );
      }
      if (sort) {
        users = [...users].sort((a, b) => {
          const cmp = sortValue(a).localeCompare(sortValue(b));
          return order === 'desc' ? -cmp : cmp;
        });
      }
      // Serialize to the real wire shape (roles[] / PascalCase status / assignedBrands)
      // the legacy CustomUserResponseFormatter emits — same contract the FE consumes.
      return response(request.url, users.map(toWireSettingsUser), {
        status: 'success',
        code: 'ok',
        error: { details: [] },
        pagination: { cursor: '', hasMore: false },
      });
    }

    const supplierContacts = supplierContactsMap[orgId];
    if (supplierContacts) {
      const data = supplierContacts
        .filter(u => u.name.toLowerCase().includes(search))
        .map(u => ({ id: u.id, name: u.name, accessToAllBrands: true, assignedBrands: [] }));
      return response(request.url, data, {
        status: 'success',
        code: 'ok',
        error: { details: [] },
      });
    }

    // DL-62766 — Contact Person (Mapping Request Step 1) dropdown data-sourcing epic.
    // Deterministic, orgId-keyed active/inactive user sets (see CONTACT_PERSON_WIZARD_ORG_IDS
    // above) so specs can assert the active-only filter, zero/one/many cardinalities, and
    // disjoint supplier sets without depending on the unfiltered generic fallback below. The
    // active filter happens HERE (server-side), matching the real backend's `OrganizationUserQuery`
    // + `UserStatus.Active` guard — the FE hook applies no client-side status filtering of its own.
    const contactPersonWizardUsers = contactPersonWizardUsersMap[orgId];
    if (contactPersonWizardUsers) {
      const data = contactPersonWizardUsers
        .filter(u => u.isActive)
        .map(u => ({ id: u.id, name: u.name, email: u.email }));
      return response(request.url, data, {
        status: 'success',
        code: 'ok',
        error: { details: [] },
      });
    }

    // Fallback — replicates purchaseOrders.ts's generic filter-dropdown behaviour, extended with
    // DL-62980's deterministic, large (180+) volume so the PO Overview's `Created by` /
    // `Supplier contact` filter dropdowns have a backing set search can meaningfully narrow (see
    // `mocks/data/purchaseOrderFilterVolume.ts` header — the epic's own stated prerequisite).
    if (schema.db.apiUsers.length === 0) {
      generateUsers(10).forEach((user: Record<string, unknown>) => schema.db.apiUsers.insert(user));
      FILTER_VOLUME_USERS.forEach(user => schema.db.apiUsers.insert(user));
    }
    let fallbackData = schema.db.apiUsers.map((record: Record<string, unknown>) => ({
      ...record,
      id: record.id,
    }));
    if (search) {
      fallbackData = fallbackData.filter((user: Record<string, unknown>) => {
        const name = ((user.name as string) ?? '').toLowerCase();
        const email = ((user.email as string) ?? '').toLowerCase();
        return name.includes(search) || email.includes(search);
      });
    }
    return response(request.url, fallbackData, {
      status: 'success',
      code: 'ok',
      error: { details: [] },
    });
  });
};
